import {
  checkHasAssets,
  initializeAms,
  loadWebToolItems,
  initPrimaryNavOptions,
  setLicenseProductRowsDescriptor,
  setBrazeCustomAttributes,
  switchAccountResetAms,
  initBrazeInAppMessages,
  openBrazeSession,
  loadAccountSummary,
  loadUserInfo,
  setBrazeAttribute,
  setTheme,
} from './ams.actions';
import {
  DEFAULT_REGION,
  updateRegion,
  updateWebToolItems,
  updatePrimaryNavOptions,
  updateLicenseProductRowsDescriptor,
  switchAccountResetAmsStore,
  updateAmsState,
  updateUnfilteredAssetSize,
  trackAmsRequests,
  updateAccountSummary,
  loadedAccountSummary$,
  updateUserInfo,
  DEFAULT_PCMWS_DATASET,
  updatePcmWsDataSet,
  DEFAULT_MAPS_SDK_REGION,
  updateMapsSdkRegion,
  updateTheme,
} from './ams.repository';
import {
  initializeAppSettings,
  loadAccountLanguage,
  loadLanguages,
  setLinkedDataState,
} from '../app-settings/app-settings.actions';
import {
  appSettingsState$,
  companySettingsSnapshot,
} from '../app-settings/app-settings.repository';
import { Region } from '../../models/region';
import { mapDataVersionToRegion } from '../../utils/map-data-version-to-region';
import { featureFlagsInitialized$ } from '../feature-flags/feature-flags.repository';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { PrimaryNavOption } from '../../models/primary-nav-option';
import { PrimaryNavOptions } from '../../models/primary-nav-options';
import { PrimaryNavKey } from '../../models/primary-nav-key';
import { updateBrazeCustomAttributes } from '../../utils/update-braze-custom-attributes';
import { appStoreSnapshot } from '../account-manager/account-manager.repository';
import { initializeAmsFeatures } from '../ams-features/ams-features.actions';
import { amsFeaturesStoreSnapshot } from '../ams-features/ams-features.repository';
import { initSecondaryNavCfs } from '../../features/cfs/utils/init-secondary-nav-cfs';
import { initSecondaryNavManageAccount } from '../../features/manage-account/utils/init-secondary-nav-manage-account';
import { initSecondaryNavDiagnostics } from '../../features/diagnostics/utils/init-secondary-nav-diagnostics';
import { PcmWsDataSet } from '../../models/pcmws-data-set';
import { MapsSdkRegion } from '../../models/maps-sdk-region';
import { setFlagsmithUserTrait } from '../feature-flags/feature-flags.actions';
import { initSecondaryNavReporting } from '../../features/reporting/utils/init-secondary-nav-reporting';
import { insertBrazeSurveyStyleTag } from '../utils/insert-braze-survey-style-tag';
import { translate } from '@jsverse/transloco';
import * as braze from '@braze/web-sdk';
import {
  LoadingRowsDescriptor,
  RowDescriptor,
  SelectAllState,
  UpdateRowsDescriptor,
} from '@tmx/operandi';
import {
  AccountsApi,
  AuthwsApi,
  TrimbleIdentityApi,
  UserPagedCollectionParams,
} from '@tmx/data-access';
import {
  map,
  switchMap,
  tap,
  combineLatest,
  BehaviorSubject,
  take,
  filter,
} from 'rxjs';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';

const initializeAms$ = createEffect((actions) =>
  actions.pipe(
    ofType(initializeAms),
    switchMap(() => {
      dispatch(initializeAppSettings());
      dispatch(loadAccountSummary());
      dispatch(checkHasAssets());
      dispatch(loadWebToolItems());
      dispatch(loadUserInfo());

      return combineLatest([
        appSettingsState$.pipe(
          tap((state) => {
            let region: Region = DEFAULT_REGION;
            let pcmWsDataSet: PcmWsDataSet = DEFAULT_PCMWS_DATASET;
            let mapsSdkRegion: MapsSdkRegion = DEFAULT_MAPS_SDK_REGION;
            if (state === 'initialized') {
              const { dataVersion } = companySettingsSnapshot();
              if (dataVersion !== null) {
                const {
                  region: mappedRegion,
                  pcmWsDataSet: mappedDataSet,
                  mapsSdkRegion: mappedMapsSdkRegion,
                } = mapDataVersionToRegion(dataVersion);

                region = mappedRegion;
                pcmWsDataSet = mappedDataSet;
                mapsSdkRegion = mappedMapsSdkRegion;
              }
            }
            updateRegion(region);
            updatePcmWsDataSet(pcmWsDataSet);
            updateMapsSdkRegion(mapsSdkRegion);

            dispatch(setFlagsmithUserTrait({ key: 'region', value: region }));
          }),
          filter((state) => state !== 'loading'),
          map((state) => state === 'initialized')
        ),
        loadedAccountSummary$.pipe(take(1)),
        featureFlagsInitialized$,
      ]).pipe(
        tap((results) => {
          const initialized = results.every((isInitialized) => isInitialized);
          dispatch(initializeAmsFeatures());
          updateAmsState(initialized ? 'initialized' : 'failed');
          dispatch(loadWebToolItems());
          dispatch(setBrazeCustomAttributes());
          dispatch(loadAccountLanguage());
          dispatch(loadLanguages());
          dispatch(setLinkedDataState());
        })
      );
    })
  )
);

const loadAccountSummary$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadAccountSummary),
    switchMap(() =>
      AccountsApi.getSummary().pipe(
        trackAmsRequests(['load-account-summary']),
        tapResult((accountSummary) => {
          updateAccountSummary(accountSummary);
        })
      )
    )
  )
);

const checkHasAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkHasAssets),
    switchMap(() =>
      AccountsApi.getUsers(
        new UserPagedCollectionParams({ offset: 0, limit: 1 })
      ).pipe(
        trackAmsRequests(['check-has-assets']),
        tapResult(({ unfilteredSize }) => {
          updateUnfilteredAssetSize(unfilteredSize);
        })
      )
    )
  )
);

const setLicenseProductRowsDescriptor$ = createEffect((actions) =>
  actions.pipe(
    ofType(setLicenseProductRowsDescriptor),
    tap(({ loading, licenseProductList }) => {
      if (loading) {
        updateLicenseProductRowsDescriptor(new LoadingRowsDescriptor());
        return;
      }

      const rows = licenseProductList.map((license) => {
        license.available$ = new BehaviorSubject(license.availableLicenses);
        return new RowDescriptor(license, (rd) => rd.data.productId, {
          isDisabled: !license.isChecked && license.availableLicenses <= 0,
        });
      });

      updateLicenseProductRowsDescriptor(
        rows.length === 0
          ? EMPTY_ROWS_DESCRIPTOR
          : new UpdateRowsDescriptor(
              rows,
              0,
              rows.length,
              0,
              SelectAllState.Deselected
            )
      );
    })
  )
);

const loadWebToolItems$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadWebToolItems),
    switchMap(() =>
      AuthwsApi.getUsersProducts().pipe(
        trackAmsRequests(['load-webtool-items'])
      )
    ),
    tapResult((items) => {
      updateWebToolItems(items);
    })
  )
);

const initPrimaryNavOptions$ = createEffect((actions) =>
  actions.pipe(
    ofType(initPrimaryNavOptions),
    tapResult(() => {
      const {
        showAssets,
        showCfs,
        showDashboard,
        showDiagnostics,
        showFleetAccounts,
        showManageAccount,
        showReporting,
        showWeather,
      } = amsFeaturesStoreSnapshot();

      updatePrimaryNavOptions(
        new PrimaryNavOptions([
          new PrimaryNavOption(
            PrimaryNavKey.Dashboard,
            'dashboard',
            translate('ams.ams.sideNavDashboard'),
            showDashboard
          ),
          new PrimaryNavOption(
            PrimaryNavKey.Accounts,
            'fleetaccounts',
            translate('ams.ams.sideNavFleetAccounts'),
            showFleetAccounts
          ),
          new PrimaryNavOption(
            PrimaryNavKey.Assets,
            'assets',
            translate('ams.ams.sideNavAssets'),
            showAssets
          ),
          new PrimaryNavOption(
            PrimaryNavKey.Cfs,
            'cfs',
            translate('ams.ams.configureFleetSettings'),
            showCfs
          ),
          new PrimaryNavOption(
            PrimaryNavKey.ManageAccount,
            'manage-account',
            translate('ams.ams.sideNavManageAccount'),
            showManageAccount
          ),
          new PrimaryNavOption(
            PrimaryNavKey.Weather,
            'weather',
            translate('ams.ams.weather'),
            showWeather
          ),
          new PrimaryNavOption(
            PrimaryNavKey.Diagnostics,
            'diagnostics',
            translate('ams.ams.sideNavDiagnostics'),
            showDiagnostics
          ),
          new PrimaryNavOption(
            PrimaryNavKey.Reporting,
            'reporting',
            translate('ams.ams.sideNavReporting'),
            showReporting
          ),
        ])
      );
    }),
    tap(() => {
      initSecondaryNavCfs();
      initSecondaryNavManageAccount();
      initSecondaryNavReporting();
      initSecondaryNavDiagnostics();
    })
  )
);

const switchAccountResetAms$ = createEffect((actions) =>
  actions.pipe(
    ofType(switchAccountResetAms),
    tapResult(() => {
      switchAccountResetAmsStore();
    })
  )
);

const setBrazeCustomAttributes$ = createEffect((actions) =>
  actions.pipe(
    ofType(setBrazeCustomAttributes),
    tapResult(() => {
      updateBrazeCustomAttributes();
    })
  )
);

const initBrazeInAppSurvey$ = createEffect((actions) =>
  actions.pipe(
    ofType(initBrazeInAppMessages),
    tapResult(() => {
      if (appStoreSnapshot().brazeInitialized) {
        braze.subscribeToInAppMessage((message) => {
          if (message instanceof braze.HtmlMessage) {
            message.message = insertBrazeSurveyStyleTag(message.message ?? '');
          }

          braze.showInAppMessage(message);
        });
      }
    })
  )
);

const openBrazeSession$ = createEffect((actions) =>
  actions.pipe(
    ofType(openBrazeSession),
    tapResult(() => {
      braze.openSession();

      braze.requestContentCardsRefresh();
    })
  )
);

const loadUserInfo$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadUserInfo),
    switchMap(() =>
      TrimbleIdentityApi.getUserinfo().pipe(
        tapResult((userInfo) => {
          updateUserInfo(userInfo);
        })
      )
    )
  )
);

const setBrazeAttribute$ = createEffect((actions) =>
  actions.pipe(
    ofType(setBrazeAttribute),
    tap(({ key, value }) => {
      const brazeUser = braze.getUser();

      if (!brazeUser) {
        return;
      }

      brazeUser.setCustomUserAttribute(key, value);
    })
  )
);

const setTheme$ = createEffect((actions) =>
  actions.pipe(
    ofType(setTheme),
    tapResult(({ theme }) => {
      updateTheme(theme);
    })
  )
);

const effects = [
  initializeAms$,
  loadAccountSummary$,
  checkHasAssets$,
  loadWebToolItems$,
  initPrimaryNavOptions$,
  setLicenseProductRowsDescriptor$,
  switchAccountResetAms$,
  setBrazeCustomAttributes$,
  initBrazeInAppSurvey$,
  openBrazeSession$,
  loadUserInfo$,
  setBrazeAttribute$,
  setTheme$,
];

export function registerAmsEffects() {
  registerEffects(effects);
}

export function deregisterAmsEffects() {
  removeEffects(effects);
}

/**
 * t(manageAccount.manageAccount.companySettingsSaved)
 * t(manageAccount.manageAccount.companySettingsNotSaved)
 */
